import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";

import { RequestError } from "../api/api-connector";

/**
 * Describe an error in one line, for screens that show the reason inline
 * rather than in the global dialog. Pure — call it during render so the text
 * follows the active locale, and pair it with `maybeReportError` at the point
 * the error is caught so unexpected ones are still reported.
 */
export function errorText(error: unknown): string {
  if (isProbablyNetworkError(error)) {
    return i18n._(
      msg`Connection error. Please check your internet connection and try again.`,
    );
  }

  if (error instanceof RequestError) {
    return extractDetailMessage(error.data);
  }

  return i18n._(
    msg`An unexpected error occurred. Please try again or contact support.`,
  );
}

/**
 * Pull the human-readable part out of an API error body. Handles both the
 * plain `{detail: "..."}` shape and FastAPI's validation shape
 * (`{detail: [{msg: "..."}]}`), and falls back to a generic message when it
 * finds neither.
 */
export function extractDetailMessage(data: unknown): string {
  const detail = (data as Record<string, unknown> | null | undefined)?.detail;

  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail) && detail.length > 0) {
    const messages = detail
      .map((item) =>
        typeof (item as Record<string, unknown>)?.msg === "string"
          ? (item as Record<string, unknown>).msg
          : null,
      )
      .filter((msg): msg is string => msg !== null);

    if (messages.length > 0) {
      return messages.join("\n");
    }
  }

  return i18n._(msg`The server returned an error.`);
}

/** What a failed `fetch` throws: Chrome, Firefox and Safari word it differently. */
const NETWORK_ERROR_MESSAGES = new Set([
  "Failed to fetch",
  "NetworkError when attempting to fetch resource.",
  "Load failed",
]);

export function isProbablyNetworkError(error: unknown) {
  return (
    error instanceof TypeError && NETWORK_ERROR_MESSAGES.has(error.message)
  );
}
